/* EVB-Konfigurator – behaviour for sections/evb-konfigurator.liquid
   - generic option groups (any product, any option names), prices always from variants
   - order = lines (one per variant) with quantities; several selections per order, direct checkout
   - selection mirrors the cart (/cart.js): restored on load and after back/forward navigation,
     updated when the cart drawer changes or removes lines (shopify:cart:lines-update)
   - ?variant=<id> in the URL preselects the initial selection (cost calculator CTA, product URLs, ad links);
     cart lines of the product win
   - checkout replaces the cart: other products (e.g. the second EVB product) are cleared first
   - validity calculator with server date
   - Theme Editor integration (shopify:block:select) */
(function () {
  'use strict';

  if (customElements.get('evb-konfigurator')) return;

  var MAX_QTY = 99;
  var ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ESC[c]; });
  }

  /* Shopify money formatting (cents + shop.money_format) */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '{{amount}}';

    function toFixed(number, precision) {
      return (Math.round(number * Math.pow(10, precision)) / Math.pow(10, precision)).toFixed(precision);
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = toFixed(number / 100.0, precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    var type = match ? match[1] : 'amount';
    switch (type) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ---------------- date helpers (calendar days, local time) ---------------- */
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function isoDate(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
  function parseIso(s) {
    var p = String(s || '').split('-').map(Number);
    if (p.length !== 3 || p.some(isNaN)) return null;
    return new Date(p[0], p[1] - 1, p[2]);
  }
  function addDays(d, n) { var x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x; }
  function sameDay(a, b) { return isoDate(a) === isoDate(b); }

  /* Converts the HTTP Date header (UTC) into the shop-local calendar date using
     the UTC offset the shop rendered (e.g. "+0200"). */
  function shopDateFromHeader(dateHeader, tzOffset) {
    var utc = new Date(dateHeader);
    if (isNaN(utc.getTime())) return null;
    var m = /^([+-])(\d\d)(\d\d)$/.exec(tzOffset || '');
    var offsetMin = m ? (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) : 0;
    var shifted = new Date(utc.getTime() + offsetMin * 60000);
    return new Date(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate());
  }

  function clampQty(n) {
    n = parseInt(n, 10);
    if (isNaN(n) || n < 1) return 1;
    return Math.min(MAX_QTY, n);
  }

  /* ======================================================================= */
  var EvbKonfigurator = function () {
    return Reflect.construct(HTMLElement, [], EvbKonfigurator);
  };
  EvbKonfigurator.prototype = Object.create(HTMLElement.prototype);
  EvbKonfigurator.prototype.constructor = EvbKonfigurator;
  Object.setPrototypeOf(EvbKonfigurator, HTMLElement);

  EvbKonfigurator.prototype.connectedCallback = function () {
    var self = this;
    var d = this.dataset;
    this.cfg = {
      moneyFormat: d.moneyFormat || '{{amount}} €',
      locale: d.locale || 'de',
      cartAddUrl: d.cartAddUrl || '/cart/add',
      cartClearUrl: d.cartClearUrl || '/cart/clear',
      cartUrl: d.cartUrl || '/cart',
      checkoutUrl: d.checkoutUrl || '/checkout',
      checkoutDirect: d.checkoutDirect === 'true',
      ctaShowPrice: d.ctaShowPrice === 'true',
      stickyCta: d.stickyCta === 'true',
      multi: d.multi === 'true',
      showQuantity: d.showQuantity === 'true',
      hideSingle: d.hideSingle === 'true',
      showDeltas: d.showDeltas === 'true',
      evbDays: parseInt(d.evbDays, 10),
      coverDays: parseInt(d.coverDays, 10),
      today: d.today || isoDate(new Date()),
      tzOffset: d.tzOffset || '+0000',
      analytics: d.analytics === 'true',
      designMode: d.designMode === 'true'
    };

    var json = this.querySelector('[data-evb-product]');
    this.product = null;
    try { this.product = json ? JSON.parse(json.textContent) : null; } catch (e) { this.product = null; }

    this.form = this.querySelector('[data-evb-form]');
    this.groups = Array.prototype.slice.call(this.querySelectorAll('[data-evb-group]'));
    this.configEl = this.querySelector('[data-evb-config]');
    this.ctaEl = this.querySelector('[data-evb-cta]');

    if (this.product && this.form) {
      var preselect = this.initState();
      this.bindForm();
      this.render();
      if (preselect) this.emit('preselect', { variant_id: preselect.id });
      if (this.cfg.stickyCta) this.initSticky();
      this.hydrateFromCart();
      this.onPageShow = function (e) { if (e.persisted) self.hydrateFromCart(); };
      window.addEventListener('pageshow', this.onPageShow);
      /* the cart drawer announces every quantity change and removal; wait for the request, then re-read the cart */
      this.onCartLinesUpdate = function (e) {
        var pending = e && e.detail && e.detail.promise;
        (pending && typeof pending.then === 'function' ? pending.catch(function () { /* still re-read */ }) : Promise.resolve())
          .then(function () { self.hydrateFromCart(); });
      };
      document.addEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
    }

    this.initCalculator();
    if (this.cfg.designMode) this.initEditor();
  };

  EvbKonfigurator.prototype.disconnectedCallback = function () {
    if (this.sticky && this.sticky.parentNode) this.sticky.parentNode.removeChild(this.sticky);
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
    if (this.onCartLinesUpdate) document.removeEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
    if (this.editorHandlers) {
      document.removeEventListener('shopify:block:select', this.editorHandlers.select);
      document.removeEventListener('shopify:block:deselect', this.editorHandlers.deselect);
    }
  };

  /* ---------------- state: lines = [{ sel: {optionPosition: value}, qty }] ---------------- */
  EvbKonfigurator.prototype.defaultSelection = function () {
    var p = this.product, first = null;
    for (var i = 0; i < p.variants.length; i++) { if (p.variants[i].available) { first = p.variants[i]; break; } }
    if (!first) first = p.variants[0];
    var sel = {};
    p.options.forEach(function (opt, idx) { sel[opt.position] = first ? first.options[idx] : opt.values[0]; });
    return sel;
  };

  EvbKonfigurator.prototype.selectionFromVariant = function (variant) {
    var sel = {};
    this.product.options.forEach(function (opt, idx) { sel[opt.position] = variant.options[idx]; });
    return sel;
  };

  /* Variant named by ?variant=<id> (cost calculator CTA, Shopify product URLs, ad deep links) when the
     id is numeric and belongs to an available variant of this product, otherwise null. Only reads the URL. */
  EvbKonfigurator.prototype.urlVariant = function () {
    var id = new URLSearchParams(window.location.search).get('variant');
    if (!id || !/^\d+$/.test(id)) return null;
    var variants = this.product.variants;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === id) return variants[i].available ? variants[i] : null;
    }
    return null;
  };

  /* The first call starts from the URL variant (if any) and returns it, later calls (cart reset) from the
     default. The URL preselect only shapes the initial state: cart lines of this product still replace
     it (applyCart), the cart wins. */
  EvbKonfigurator.prototype.initState = function () {
    var preselect = this.urlPreselectDone ? null : this.urlVariant();
    this.urlPreselectDone = true;
    this.lines = [{ sel: preselect ? this.selectionFromVariant(preselect) : this.defaultSelection(), qty: 1 }];
    this.active = 0;
    this.previewLine = false;
    return preselect;
  };

  EvbKonfigurator.prototype.variantFor = function (selection) {
    var p = this.product;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i], ok = true;
      for (var j = 0; j < p.options.length; j++) {
        if (v.options[j] !== selection[p.options[j].position]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  /* Keeps at most one line per variant: identical selections are merged (quantities added). */
  EvbKonfigurator.prototype.normalize = function () {
    var self = this;
    var seen = {}, out = [], activeKey = null;
    this.lines.forEach(function (line, i) {
      var v = self.variantFor(line.sel);
      var key = v ? String(v.id) : 'sel:' + JSON.stringify(line.sel);
      if (i === self.active) activeKey = key;
      if (seen[key] !== undefined) {
        out[seen[key]].qty = clampQty(out[seen[key]].qty + line.qty);
      } else {
        seen[key] = out.length;
        out.push({ sel: line.sel, qty: clampQty(line.qty) });
      }
    });
    this.lines = out;
    this.active = activeKey !== null && seen[activeKey] !== undefined ? seen[activeKey] : Math.min(this.active, out.length - 1);
  };

  EvbKonfigurator.prototype.labelFor = function (selection) {
    var parts = [];
    this.groups.forEach(function (g) {
      var pos = g.getAttribute('data-option-position');
      var tile = g.querySelector('[data-evb-tile][data-value="' + CSS.escape(selection[pos] || '') + '"]');
      var label = tile ? tile.querySelector('.evb-tile__label') : null;
      parts.push(label ? label.textContent.trim() : (selection[pos] || ''));
    });
    return parts.join(' · ');
  };

  EvbKonfigurator.prototype.money = function (cents) {
    return formatMoney(cents, this.cfg.moneyFormat);
  };

  EvbKonfigurator.prototype.emit = function (name, detail) {
    try {
      this.dispatchEvent(new CustomEvent('evb:' + name, { bubbles: true, detail: detail || {} }));
      if (this.cfg.analytics) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: 'evb_' + name }, detail || {}));
        /* Shopify's analytics channel: custom pixels (Settings > Customer events) subscribe to
           'evb_<name>' and read the detail as event.customData; the page's dataLayer is out of their reach */
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('evb_' + name, detail || {});
        }
      }
    } catch (e) { /* noop */ }
  };

  /* ---------------- cart hydration ----------------
     The form mirrors the cart lines of its product. The cart is read on load, after back/forward
     navigation and after every change the cart drawer announces. Only a change of this product's
     lines touches the form (a removed line disappears, the last one resets the form to its default),
     so an unsubmitted selection survives edits of other cart lines. */
  EvbKonfigurator.prototype.hydrateFromCart = function () {
    var self = this;
    try {
      fetch(this.cfg.cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (cart) {
          if (!cart || !cart.items) return;
          self.applyCart(cart);
        })
        .catch(function () { /* keep the current state */ });
    } catch (e) { /* noop */ }
  };

  /* Lines of this product in a cart, in cart order. */
  EvbKonfigurator.prototype.cartLinesFor = function (cart) {
    var self = this, lines = [];
    (cart.items || []).forEach(function (item) {
      if (item.product_id !== self.product.id) return;
      var variant = null;
      for (var i = 0; i < self.product.variants.length; i++) { if (self.product.variants[i].id === item.variant_id) { variant = self.product.variants[i]; break; } }
      if (variant) lines.push({ sel: self.selectionFromVariant(variant), qty: clampQty(item.quantity) });
    });
    return lines;
  };

  EvbKonfigurator.prototype.applyCart = function (cart) {
    var self = this;
    var lines = this.cartLinesFor(cart);
    var key = JSON.stringify(lines.map(function (l) { var v = self.variantFor(l.sel); return [v ? v.id : null, l.qty]; }));
    var first = this.cartKey === undefined;
    if (!first && key === this.cartKey) return;
    this.cartKey = key;
    if (!lines.length) {
      if (first) return;
      this.initState();
      this.render();
      this.emit('reset', { reason: 'cart' });
      return;
    }
    this.lines = lines;
    this.active = lines.length - 1;
    this.normalize();
    this.render();
    this.emit('restored', { line_count: lines.length });
  };

  /* ---------------- form interaction ---------------- */
  EvbKonfigurator.prototype.bindForm = function () {
    var self = this;

    this.groups.forEach(function (g) {
      var pos = g.getAttribute('data-option-position');
      g.addEventListener('click', function (e) {
        var tile = e.target.closest('[data-evb-tile]');
        if (!tile || tile.getAttribute('aria-disabled') === 'true') return;
        var value = tile.getAttribute('data-value');
        var line = self.lines[self.active];
        line.sel = Object.assign({}, line.sel);
        line.sel[pos] = value;
        self.normalize();
        self.render();
        var v = self.variantFor(self.lines[self.active].sel);
        self.emit('option_change', { option_position: Number(pos), value: value, variant_id: v ? v.id : null, line_index: self.active });
      });
      g.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowDown' && e.key !== 'ArrowLeft' && e.key !== 'ArrowUp') return;
        var tiles = Array.prototype.slice.call(g.querySelectorAll('[data-evb-tile]'));
        var idx = tiles.indexOf(document.activeElement);
        if (idx < 0) return;
        e.preventDefault();
        var next = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? (idx + 1) % tiles.length : (idx - 1 + tiles.length) % tiles.length;
        tiles[next].focus();
        tiles[next].click();
      });
    });

    if (this.configEl) {
      this.configEl.addEventListener('click', function (e) {
        var edit = e.target.closest('[data-evb-line-edit]');
        var remove = e.target.closest('[data-evb-line-remove]');
        var removeActive = e.target.closest('[data-evb-remove-active]');
        var inc = e.target.closest('[data-evb-qty-inc]');
        var dec = e.target.closest('[data-evb-qty-dec]');
        if (edit) { self.active = self.lineIndexOf(edit); self.render(); }
        if (remove) { self.removeLine(self.lineIndexOf(remove)); }
        if (removeActive) { self.removeLine(self.active); }
        if (inc || dec) {
          var idx = self.lineIndexOf(inc || dec);
          self.setQty(idx, self.lines[idx].qty + (inc ? 1 : -1));
        }
      });
      this.configEl.addEventListener('change', function (e) {
        var input = e.target.closest('[data-evb-qty-input]');
        if (!input) return;
        self.setQty(self.lineIndexOf(input), input.value);
      });
      this.configEl.addEventListener('keydown', function (e) {
        var input = e.target.closest('[data-evb-qty-input]');
        if (input && e.key === 'Enter') { e.preventDefault(); self.setQty(self.lineIndexOf(input), input.value); }
      });
    }

    var addBtn = this.querySelector('[data-evb-add-line]');
    if (addBtn) addBtn.addEventListener('click', function () { self.addLine(); });

    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.submit();
    });
  };

  /* index of the line a control belongs to: a collapsed card carries data-index, otherwise it is the active line */
  EvbKonfigurator.prototype.lineIndexOf = function (el) {
    var card = el.closest('[data-evb-line]');
    return card ? Number(card.getAttribute('data-index')) : this.active;
  };

  EvbKonfigurator.prototype.setQty = function (index, qty) {
    if (!this.lines[index]) return;
    var n = clampQty(qty);
    if (n === this.lines[index].qty) { this.render(); return; }
    this.lines[index].qty = n;
    this.render();
    this.emit('quantity_change', { line_index: index, quantity: n, total_quantity: this.totalQty() });
  };

  EvbKonfigurator.prototype.totalQty = function () {
    return this.lines.reduce(function (s, l) { return s + l.qty; }, 0);
  };

  EvbKonfigurator.prototype.freeVariantExists = function () {
    var self = this, used = {};
    this.lines.forEach(function (l) { var v = self.variantFor(l.sel); if (v) used[v.id] = true; });
    return this.product.variants.some(function (v) { return v.available && !used[v.id]; });
  };

  EvbKonfigurator.prototype.addLine = function () {
    if (!this.cfg.multi || !this.freeVariantExists()) return;
    /* start from a variant that is not yet in the order so the new line is distinct */
    var self = this, used = {};
    this.lines.forEach(function (l) { var v = self.variantFor(l.sel); if (v) used[v.id] = true; });
    var candidate = null;
    for (var i = 0; i < this.product.variants.length; i++) {
      var v = this.product.variants[i];
      if (v.available && !used[v.id]) { candidate = v; break; }
    }
    this.lines.push({ sel: candidate ? this.selectionFromVariant(candidate) : Object.assign({}, this.lines[this.active].sel), qty: 1 });
    this.active = this.lines.length - 1;
    this.normalize();
    this.render();
    this.emit('line_add', { line_count: this.lines.length });
    var head = this.querySelector('[data-evb-active-head]');
    if (head && typeof head.scrollIntoView === 'function') head.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  EvbKonfigurator.prototype.removeLine = function (index) {
    if (this.lines.length <= 1) return;
    this.lines.splice(index, 1);
    if (this.active > index) this.active -= 1;
    if (this.active > this.lines.length - 1) this.active = this.lines.length - 1;
    this.render();
    this.emit('line_remove', { line_count: this.lines.length });
  };

  /* ---------------- rendering ---------------- */
  EvbKonfigurator.prototype.render = function () {
    var self = this;
    var multi = this.lines.length > 1;
    var activeLine = this.lines[this.active];
    var activeSel = activeLine.sel;
    var activeVariant = this.variantFor(activeSel);
    var activePrice = activeVariant ? activeVariant.price : 0;

    /* tiles */
    this.groups.forEach(function (g) {
      var pos = g.getAttribute('data-option-position');
      Array.prototype.forEach.call(g.querySelectorAll('[data-evb-tile]'), function (tile) {
        var value = tile.getAttribute('data-value');
        var checked = activeSel[pos] === value;
        tile.setAttribute('aria-checked', checked ? 'true' : 'false');
        tile.tabIndex = checked ? 0 : -1;
        var candidate = Object.assign({}, activeSel); candidate[pos] = value;
        var v = self.variantFor(candidate);
        var delta = tile.querySelector('[data-evb-delta]');
        if (!v || !v.available) {
          tile.setAttribute('aria-disabled', 'true');
          if (delta) delta.textContent = tile.getAttribute('data-unavailable-text') || '';
        } else {
          tile.removeAttribute('aria-disabled');
          if (delta) {
            var diff = v.price - activePrice;
            delta.textContent = (self.cfg.showDeltas && activeVariant && diff !== 0)
              ? (diff > 0 ? '+ ' : '− ') + self.money(Math.abs(diff))
              : '';
          }
        }
      });
      var anyChecked = g.querySelector('[data-evb-tile][aria-checked="true"]');
      if (!anyChecked) { var firstTile = g.querySelector('[data-evb-tile]'); if (firstTile) firstTile.tabIndex = 0; }
    });

    /* totals */
    var total = 0, allAvailable = true, items = {}, totalQty = 0;
    this.lines.forEach(function (line) {
      var v = self.variantFor(line.sel);
      if (!v || !v.available) { allAvailable = false; return; }
      total += v.price * line.qty;
      totalQty += line.qty;
      items[v.id] = (items[v.id] || 0) + line.qty;
    });
    this.total = total;
    this.items = items;
    this.allAvailable = allAvailable;

    /* hidden inputs for the no-JS fallback */
    var idInput = this.form.querySelector('[data-evb-variant-id]');
    if (idInput && activeVariant) idInput.value = activeVariant.id;
    var qtyInput = this.form.querySelector('input[name="quantity"]');
    if (qtyInput) qtyInput.value = activeLine.qty;

    /* top price */
    var priceDisplay = this.configEl ? this.configEl.getAttribute('data-price-display') : 'current';
    var top = this.querySelector('[data-evb-top-price]');
    if (top) top.textContent = this.money(priceDisplay === 'total' ? total : activePrice);
    var topVehicle = this.querySelector('[data-evb-top-vehicle]');
    if (topVehicle) topVehicle.hidden = true;

    /* line cards + active head + quantity */
    var lineLabel = this.configEl ? (this.configEl.getAttribute('data-vehicle-label') || 'Auswahl') : 'Auswahl';
    if (this.configEl) {
      this.configEl.classList.toggle('evb-config--multi', multi);
      var before = this.configEl.querySelector('[data-evb-lines-before]');
      var after = this.configEl.querySelector('[data-evb-lines-after]');
      var tpl = this.configEl.querySelector('[data-evb-line-card]');
      before.innerHTML = ''; after.innerHTML = '';
      if (multi && tpl) {
        this.lines.forEach(function (line, i) {
          if (i === self.active) return;
          var v = self.variantFor(line.sel);
          var node = tpl.content.firstElementChild.cloneNode(true);
          node.setAttribute('data-index', i);
          node.querySelector('[data-evb-line-title]').textContent = self.labelFor(line.sel);
          node.querySelector('[data-evb-line-price]').textContent = v ? self.money(v.price * line.qty) : '';
          var qi = node.querySelector('[data-evb-qty-input]');
          if (qi) qi.value = line.qty;
          var dec = node.querySelector('[data-evb-qty-dec]');
          if (dec) dec.disabled = line.qty <= 1;
          var count = node.querySelector('[data-evb-line-count]');
          if (count) count.textContent = line.qty + ' ×';
          (i < self.active ? before : after).appendChild(node);
        });
      }
      var head = this.configEl.querySelector('[data-evb-active-head]');
      if (head) {
        head.hidden = !multi;
        var t = head.querySelector('[data-evb-active-title]');
        var pr = head.querySelector('[data-evb-active-price]');
        if (t) t.textContent = lineLabel + ' ' + (this.active + 1);
        if (pr) pr.textContent = this.money(activePrice * activeLine.qty);
      }
      var activeQty = this.configEl.querySelector('[data-evb-active] [data-evb-qty-input]');
      if (activeQty) activeQty.value = activeLine.qty;
      var activeDec = this.configEl.querySelector('[data-evb-active] [data-evb-qty-dec]');
      if (activeDec) activeDec.disabled = activeLine.qty <= 1;
      var activeInc = this.configEl.querySelector('[data-evb-active] [data-evb-qty-inc]');
      if (activeInc) activeInc.disabled = activeLine.qty >= MAX_QTY;
    }

    /* add button */
    var addBtn = this.querySelector('[data-evb-add-line]');
    var limit = this.querySelector('[data-evb-add-limit]');
    if (addBtn) {
      var exhausted = !this.freeVariantExists();
      addBtn.disabled = exhausted;
      if (limit) limit.hidden = !exhausted;
    }

    /* CTA */
    if (this.ctaEl) {
      var btn = this.ctaEl.querySelector('[data-evb-submit]');
      var label = this.ctaEl.querySelector('[data-evb-cta-label]');
      var price = this.ctaEl.querySelector('[data-evb-cta-price]');
      var note = this.ctaEl.querySelector('[data-evb-cta-note]');
      if (!this.ctaLabelDefault && label) this.ctaLabelDefault = label.textContent;
      if (btn) btn.disabled = !allAvailable;
      if (label) label.textContent = allAvailable ? this.ctaLabelDefault : (this.ctaEl.getAttribute('data-sold-out') || this.ctaLabelDefault);
      if (price) price.textContent = this.money(total);
      if (note) {
        var tplNote = totalQty > 1 ? (this.ctaEl.getAttribute('data-note-multi') || '') : (this.ctaEl.getAttribute('data-note') || '');
        note.textContent = tplNote.replace('[anzahl]', String(totalQty)).replace('[gesamt]', this.money(total));
        note.hidden = !tplNote;
      }
    }

    this.renderSticky();
  };

  /* ---------------- submit ---------------- */
  EvbKonfigurator.prototype.submit = function () {
    var self = this;
    if (!this.allAvailable || this.submitting) return;
    var items = Object.keys(this.items).map(function (id) { return { id: Number(id), quantity: self.items[id] }; });
    if (!items.length) return;

    var btn = this.ctaEl ? this.ctaEl.querySelector('[data-evb-submit]') : null;
    var err = this.ctaEl ? this.ctaEl.querySelector('[data-evb-cta-error]') : null;
    this.submitting = true;
    if (btn) btn.classList.add('is-loading');
    if (err) err.hidden = true;

    /* The form is the order: the cart is cleared first, so it never carries a stale line or the
       other EVB product next to this one. */
    var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    fetch(this.cfg.cartClearUrl + '.js', { method: 'POST', headers: headers, credentials: 'same-origin' })
      .then(function () {
        return fetch(self.cfg.cartAddUrl + '.js', { method: 'POST', headers: headers, credentials: 'same-origin', body: JSON.stringify({ items: items }) });
      })
      .then(function (res) {
        return res.json().then(function (data) { if (!res.ok) throw data; return data; });
      })
      .then(function () {
        self.emit('checkout', { line_count: self.lines.length, total_quantity: self.totalQty(), total: self.total, items: items });
        window.location.href = self.cfg.checkoutDirect ? self.cfg.checkoutUrl : self.cfg.cartUrl;
      })
      .catch(function (data) {
        self.submitting = false;
        if (btn) btn.classList.remove('is-loading');
        if (err) {
          err.textContent = (data && (data.description || data.message)) || 'Fehler beim Hinzufügen. Bitte erneut versuchen.';
          err.hidden = false;
        }
      });
  };

  /* ---------------- sticky CTA (mobile) ---------------- */
  EvbKonfigurator.prototype.initSticky = function () {
    var self = this;
    var btn = this.ctaEl ? this.ctaEl.querySelector('[data-evb-submit]') : null;
    if (!btn) return;
    var sticky = document.createElement('div');
    sticky.className = 'evb-sticky';
    sticky.setAttribute('aria-hidden', 'true');
    sticky.innerHTML =
      '<div class="evb-sticky__info"><span class="evb-sticky__price" data-evb-sticky-price></span><span class="evb-sticky__summary" data-evb-sticky-summary></span></div>' +
      '<button type="button" class="evb-sticky__button" data-evb-sticky-submit></button>';
    this.appendChild(sticky);
    this.sticky = sticky;
    sticky.querySelector('[data-evb-sticky-submit]').addEventListener('click', function () {
      if (typeof self.form.requestSubmit === 'function') self.form.requestSubmit(); else self.submit();
    });
    var ticking = false;
    var update = function () {
      ticking = false;
      var rect = btn.getBoundingClientRect();
      var formRect = self.form.getBoundingClientRect();
      /* visible once the checkout button has scrolled above the viewport and the form is still (partly) on the page */
      var show = rect.bottom < 0 && formRect.bottom > -400;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    };
    this.onScroll = function () { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    update();
    this.renderSticky();
  };

  EvbKonfigurator.prototype.renderSticky = function () {
    if (!this.sticky) return;
    var label = this.ctaEl ? this.ctaEl.querySelector('[data-evb-cta-label]') : null;
    this.sticky.querySelector('[data-evb-sticky-price]').textContent = this.money(this.total || 0);
    var qty = this.totalQty();
    var summary = this.lines.length > 1
      ? (qty + ' × EVB')
      : ((qty > 1 ? qty + ' × ' : '') + this.labelFor(this.lines[this.active].sel));
    this.sticky.querySelector('[data-evb-sticky-summary]').textContent = summary;
    var b = this.sticky.querySelector('[data-evb-sticky-submit]');
    b.textContent = label ? label.textContent : '';
    b.disabled = !this.allAvailable;
  };

  /* ---------------- validity calculator ---------------- */
  EvbKonfigurator.prototype.initCalculator = function () {
    var self = this;
    var el = this.querySelector('[data-evb-calc]');
    if (!el) return;
    this.calc = {
      el: el,
      today: parseIso(this.cfg.today) || new Date(),
      order: null,
      reg: null,
      openField: null,
      editable: el.getAttribute('data-editable') === 'true'
    };

    el.addEventListener('click', function (e) {
      var tok = e.target.closest('[data-evb-tok]');
      if (!tok || !self.calc.editable) return;
      var which = tok.getAttribute('data-evb-tok');
      self.calc.openField = self.calc.openField === which ? null : which;
      self.renderCalculator();
      if (self.calc.openField) {
        var input = el.querySelector('[data-evb-date="' + which + '"]');
        if (input) { try { input.focus(); if (typeof input.showPicker === 'function') input.showPicker(); } catch (err) { /* noop */ } }
      }
    });

    el.addEventListener('change', function (e) {
      var input = e.target.closest('[data-evb-date]');
      if (!input) return;
      var d = parseIso(input.value);
      if (!d) return;
      var which = input.getAttribute('data-evb-date');
      if (which === 'order') self.calc.order = d; else self.calc.reg = d;
      self.calc.openField = null;
      self.renderCalculator();
    });

    this.renderCalculator();

    /* correct "today" with the server time (cached pages may carry a stale date) */
    try {
      fetch(this.cfg.cartUrl + '.js', { method: 'HEAD', cache: 'no-store', credentials: 'same-origin' })
        .then(function (res) {
          var header = res.headers.get('date');
          var d = header ? shopDateFromHeader(header, self.cfg.tzOffset) : null;
          if (d && !sameDay(d, self.calc.today)) { self.calc.today = d; self.renderCalculator(); }
        })
        .catch(function () { /* keep server-rendered date */ });
    } catch (e) { /* noop */ }
  };

  EvbKonfigurator.prototype.renderCalculator = function () {
    var c = this.calc, el = c.el, cfg = this.cfg;
    var today = c.today;
    var order = c.order || today;
    var reg = c.reg || order;
    var evbEnd = addDays(order, cfg.evbDays);
    var coverEnd = addDays(reg, Math.max(0, cfg.coverDays - 1));
    var fmt = new Intl.DateTimeFormat(cfg.locale, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
    var fmtLong = new Intl.DateTimeFormat(cfg.locale, { weekday: 'long' });
    var f = function (d) { return fmt.format(d); };

    var tokToday = el.getAttribute('data-token-today') || 'Heute';
    var tokOrderday = el.getAttribute('data-token-orderday') || 'Am Bestelltag';
    var prefix = el.getAttribute('data-token-prefix') || 'Am';
    var tok1 = sameDay(order, today) ? tokToday : prefix + ' ' + f(order);
    var tok2 = sameDay(reg, today) ? tokToday : (sameDay(reg, order) ? tokOrderday : prefix + ' ' + f(reg));

    var tokenHtml = function (which, label) {
      return c.editable
        ? '<button type="button" class="evb-tok" data-evb-tok="' + which + '">' + escapeHtml(label) + '</button>'
        : '<span class="evb-tok" data-evb-tok="' + which + '" disabled>' + escapeHtml(label) + '</span>';
    };

    var s1 = escapeHtml(el.getAttribute('data-sentence-order') || '')
      .replace('[bestelldatum]', tokenHtml('order', tok1))
      .replace('[datum]', '<strong data-evb-out="evb">' + escapeHtml(f(evbEnd)) + '</strong>');
    var s2 = escapeHtml(el.getAttribute('data-sentence-reg') || '')
      .replace('[zulassungsdatum]', tokenHtml('reg', tok2))
      .replace('[datum]', '<strong data-evb-out="cover">' + escapeHtml(f(coverEnd)) + '</strong>')
      .replace('[tage]', String(cfg.coverDays));

    var p1 = el.querySelector('[data-evb-sentence="order"]');
    var p2 = el.querySelector('[data-evb-sentence="reg"]');
    if (p1) p1.innerHTML = s1;
    if (p2) p2.innerHTML = s2;

    var fOrder = el.querySelector('[data-evb-field="order"]');
    var fReg = el.querySelector('[data-evb-field="reg"]');
    if (fOrder) {
      fOrder.hidden = c.openField !== 'order';
      var iO = fOrder.querySelector('input'); if (iO) { iO.value = isoDate(order); }
    }
    if (fReg) {
      fReg.hidden = c.openField !== 'reg';
      var iR = fReg.querySelector('input'); if (iR) { iR.value = isoDate(reg); iR.min = isoDate(order); iR.max = isoDate(evbEnd); }
    }

    var warn = el.querySelector('[data-evb-calc-warn]');
    if (warn) {
      var text = '';
      if (reg > evbEnd) text = (el.getAttribute('data-too-late') || '').replace('[datum]', f(evbEnd));
      else if (reg < order) text = (el.getAttribute('data-too-early') || '');
      warn.textContent = text;
      warn.hidden = !text;
    }

    var hint = el.querySelector('[data-evb-calc-hint]');
    if (hint) {
      var dow = evbEnd.getDay();
      var weekend = el.getAttribute('data-weekend') === 'true' && (dow === 0 || dow === 6);
      if (weekend) {
        var workday = addDays(evbEnd, dow === 0 ? -2 : -1);
        hint.textContent = (el.getAttribute('data-weekend-text') || '')
          .replace('[datum]', f(evbEnd))
          .replace('[wochentag]', fmtLong.format(evbEnd))
          .replace('[werktag]', f(workday));
      }
      hint.hidden = !weekend;
    }
  };

  /* ---------------- Theme Editor ---------------- */
  EvbKonfigurator.prototype.initEditor = function () {
    var self = this;
    var select = function (e) {
      if (!self.contains(e.target)) return;
      var block = e.target.closest('[data-evb-block]');
      var type = block ? block.getAttribute('data-evb-block') : '';
      if ((type === 'weiteres-fahrzeug' || type === 'konfiguration') && self.lines && self.lines.length === 1 && self.cfg.multi) {
        self.previewLine = true;
        self.addLine();
      }
      if (type === 'rechner' && self.calc && self.calc.editable) {
        self.calc.openField = 'order';
        self.renderCalculator();
      }
      if (block && typeof block.scrollIntoView === 'function') block.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    var deselect = function (e) {
      if (!self.contains(e.target)) return;
      if (self.previewLine && self.lines && self.lines.length > 1) {
        self.previewLine = false;
        self.lines = [self.lines[0]];
        self.active = 0;
        self.render();
      }
      if (self.calc && self.calc.openField) { self.calc.openField = null; self.renderCalculator(); }
    };
    this.editorHandlers = { select: select, deselect: deselect };
    document.addEventListener('shopify:block:select', select);
    document.addEventListener('shopify:block:deselect', deselect);
  };

  customElements.define('evb-konfigurator', EvbKonfigurator);
})();
